import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import { useState } from "react";

import truncate from "truncate-html";

import { categoryMap } from "@/helpers/categories";

import {
  Box,
  Button,
  FiArrowRight,
  Flex,
  HTMLWrapper,
  Text,
  Link,
  HorizontalScrollMenu,
  IconButton,
  FiEdit,
  VStack,
  Heading,
  Contribution
} from "@/storybook-components";
import {
  dateWithSiteLocale,
  buildImgixUrl,
  stripAllHtml,
  sanitizeEngagementBannerHtml
} from "@/lib";
import { buildPrivateFileRecordImageUrl } from "@/hooks/use-private-file-display-url";
import { useIsPrivateIdeaMediaBucketEnabled } from "@/hooks/toggle";
import type { Brainstormer, Idea as IdeaType } from "@/types";
import { useAppSelector } from "@/redux/store/hooks";
import Alerts from "@/components/alerts";
import useUser from "@/hooks/useUser";
import useBudget from "@/hooks/useBudget";
import useCanCreatePost from "@/hooks/useCanCreatePost";
import useProjectPageTheme from "@/hooks/useProjectPageTheme";

import { getRandomColor } from "@/helpers/get-random-color";
import useEmbeds from "@/hooks/useEmbeds";
import { toSentenceCase } from "@/helpers/to-sentence-case";
import { getToolLocaleKeyFromLayout } from "@/helpers/get-tool-locale-key-from-layout";
import ActionList from "@/components/tools/common/action-list";

const Idea = ({
  brainstormer
}: {
  brainstormer: Partial<
    Brainstormer & {
      ideas: { meta: { count: number }; ideaList: Array<Partial<IdeaType>> };
    }
  >;
}) => {
  const router = useRouter();
  const { isEmbed, isInsideIframe, isInsideIframeAndOnCurrentUrl } =
    useEmbeds();
  const { translate, language } = useTranslator();
  const { isRegisteredUserLoggedIn } = useUser();
  const canCreatePost = useCanCreatePost(brainstormer.layout);
  const { formatBudget } = useBudget();
  const { toolBannerTitleSx } = useProjectPageTheme();
  const { settings } = useAppSelector((state) => state.settings);
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const isPrivateIdeaMediaBucketEnabled = useIsPrivateIdeaMediaBucketEnabled();
  const { backgroundColours } = useAppSelector(
    (state) => state.toolCustomizations
  );
  const { visibilityMode, canContribute } = useAppSelector(
    (state) => state.project.project
  );

  const {
    name,
    description,
    state,
    layout,
    archivalReasonMessage,
    allowCommentsForIdeas,
    toolConfigurationAttributes,
    baCategoryConfigAttributes,
    id,
    ideas,
    permalink,
    disableVoting
  } = brainstormer;
  const { meta, ideaList } = ideas;
  const { projectId } = router.query as { projectId: string };
  const unitCode = baCategoryConfigAttributes?.unitCode || "";

  const getRouteTarget = () =>
    isEmbed && isInsideIframe && !isInsideIframeAndOnCurrentUrl
      ? "_blank"
      : "_parent";

  const callToAction =
    toolConfigurationAttributes?.preferences?.callToAction || "";
  const listingHeader =
    toolConfigurationAttributes?.preferences?.listingHeader || "";

  const [showArchiveMessage, setShowArchiveMessage] = useState(
    state === "archived"
  );
  const [showDraftMessage, setShowDraftMessage] = useState(state === "draft");
  const [showPanelledVisibilityMessage, setshowPanelledVisibilityMessage] =
    useState(
      (visibilityMode === "Panelled" && !isRegisteredUserLoggedIn) ||
        (visibilityMode === "Panelled" && !canContribute)
    );

  const ideasWithExtraItem = [
    ...ideaList,
    ...(state === "archived" ||
    (visibilityMode === "Panelled" && !isRegisteredUserLoggedIn) ||
    (visibilityMode === "Panelled" && !canContribute)
      ? []
      : [
          // {
          //   id: "joinConversation"
          // }
        ])
  ];

  const joinConversationNode = (key?: string) => {
    return (
      <Flex
        key={key}
        tabIndex={0}
        align="center"
        cursor="pointer"
        justify="center"
        flexDir="column"
        borderRadius="lg"
        background="gray.200"
        height="full"
        width={{
          md: 288,
          sm: meta?.count <= 0 ? "full" : 212,
          base: meta?.count <= 0 ? "full" : 212
        }}
        onClick={() =>
          window.open(
            `/${projectId}/brainstormers/${id}?action=new_idea`,
            getRouteTarget()
          )
        }
        _focusVisible={{
          outline: "none",
          boxShadow: "outline"
        }}
      >
        <Text fontSize="lg" fontWeight={700}>
          {translate("brainstormers.joinConversation")}
        </Text>
        <Text fontWeight={400}>
          {translate("brainstormers.postYourOpinionToday")}
        </Text>
        <IconButton
          mt={4}
          round
          variant="solid"
          tabIndex={0}
          onClick={null}
          icon={<FiEdit />}
          aria-label={translate("brainstormers.createPost")}
        />
      </Flex>
    );
  };

  const colorList = ["#975A16", "#2C7A7B", "#B83280", "#6B46C1"];

  return (
    <Box py={6} mx={{ md: 6, sm: 4, base: 4 }} data-testid="ideas-banner">
      <Heading as="h1" data-testid="ideas-banner-heading">
        <Link
          target={getRouteTarget()}
          css={toolBannerTitleSx}
          rel="noopener noreferrer"
          href={`/${projectId}/brainstormers/${permalink}`}
          data-testid="ideas-banner-link"
        >
          {name}
        </Link>
      </Heading>
      <HTMLWrapper
        my={4}
        html={truncate(sanitizeEngagementBannerHtml(description), {
          length: 500
        })}
      />
      {(showDraftMessage ||
        showArchiveMessage ||
        showPanelledVisibilityMessage) && (
        <VStack>
          {showDraftMessage && (
            <Alerts
              status="info"
              description={translate("brainstormers.thisToolIsInDraft")}
              onClose={() => setShowDraftMessage(false)}
              data-testid="ideas-banner-draft-alert"
            />
          )}
          {showArchiveMessage && (
            <Alerts
              status="info"
              description={
                archivalReasonMessage ||
                translate("brainstormers.boardArchived")
              }
              onClose={() => setShowArchiveMessage(false)}
              data-testid="ideas-banner-archived-alert"
            />
          )}
          {showPanelledVisibilityMessage && (
            <Alerts
              status="info"
              description={translate("brainstormers.visibilityPanelled")}
              onClose={() => setshowPanelledVisibilityMessage(false)}
              data-testid="ideas-banner-panelled-alert"
            />
          )}
        </VStack>
      )}
      {meta?.count <= 0 ? null : (
        <>
          <Flex
            mt={12}
            justifyContent="space-between"
            data-testid="ideas-banner-list-header"
          >
            <Heading
              as="h2"
              fontSize="lg"
              fontWeight={700}
              data-testid="ideas-banner-listing-header"
            >
              {toSentenceCase(
                listingHeader.trim() ||
                  translate(
                    `tools.${getToolLocaleKeyFromLayout(layout)}.listingHeader`
                  )
              )}
            </Heading>
            {meta?.count <= 1 ? null : (
              <Button
                size="xs"
                fontWeight="500"
                variant="tertiary"
                rightIcon={<FiArrowRight />}
                onClick={() =>
                  window.open(
                    `/${projectId}/brainstormers/${permalink}`,
                    getRouteTarget()
                  )
                }
                data-testid="ideas-banner-see-all"
              >
                {translate("common.seeAll")}
              </Button>
            )}
          </Flex>
          <Box my={6}>
            <HorizontalScrollMenu
              itemHeight="full"
              px={{ md: 6, sm: 4, base: 4 }}
              itemWidth={{
                md: 288,
                sm: meta?.count === 1 ? "full" : 212,
                base: meta?.count === 1 ? "full" : 212
              }}
            >
              {ideasWithExtraItem.map((idea: any) => {
                if (idea.id === "joinConversation")
                  return joinConversationNode(idea.id);

                const categories = useAppSelector(
                  (state) => state.brainstormers.categories
                );
                const catMap = categoryMap(categories);
                const categoryName =
                  idea?.categoryId != null
                    ? catMap[String(idea.categoryId)]
                    : undefined;

                return (
                  <Link
                    key={idea.id}
                    target={getRouteTarget()}
                    display="block"
                    textDecoration="none"
                    rel="noopener noreferrer"
                    css={{ _hover: { textDecoration: "none" } }}
                    href={`/${projectId}/brainstormers/${permalink}?idea=${idea.id}`}
                    data-testid={`ideas-banner-card-${idea.id}`}
                  >
                    <Contribution
                      id={id}
                      onClick={() =>
                        window.open(
                          `/${projectId}/brainstormers/${permalink}?idea=${idea.id}`,
                          getRouteTarget()
                        )
                      }
                      cursor="pointer"
                      variant="vertical"
                      hideHeader={!canCreatePost}
                      isLoaded
                      showImage
                      isThumbsUp={!disableVoting}
                      showFooter
                      showDescription
                      showCommentIcon={allowCommentsForIdeas}
                      makeFooterViewOnly
                      imageAlt={idea.name}
                      username={idea.author}
                      cardHeading={idea.name}
                      cardDescription={
                        idea.description ? stripAllHtml(idea.description) : ""
                      }
                      showTags={!!categoryName}
                      tags={categoryName ? [categoryName] : []}
                      thumbsUpCount={idea.votesCount || 0}
                      commentsCount={idea.commentsCount || 0}
                      imageSrc={buildPrivateFileRecordImageUrl({
                        record: idea,
                        tool: "ideas",
                        user,
                        anonymousUser,
                        height: 162,
                        width: 288,
                        fit: "min",
                        isPrivateBucketEnabled: isPrivateIdeaMediaBucketEnabled
                      })}
                      userAvatarSrc={buildImgixUrl(idea.avatar)}
                      isShareIcon={!settings.disableSocialMediaSharing}
                      contributionTime={dateWithSiteLocale(
                        idea.createdAt,
                        language.toLowerCase()
                      )}
                      tileBackgroundColor={
                        idea.backgroundColourId
                          ? backgroundColours.find(
                              ({ id }) =>
                                `${idea.backgroundColourId}` === `${id}`
                            )?.name
                          : getRandomColor(colorList)
                      }
                      ariaLabelShareIcon={translate(
                        "brainstormers.shareThisPage"
                      )}
                      ariaLabelVoteIcon={`${translate("common.numberOfVotes", {
                        votesCount: idea.votesCount
                      })}`}
                      ariaLabelCommentIcon={`${translate(
                        "common.numberOfComments",
                        { commentsCount: idea.commentsCount }
                      )}`}
                      showBudgeting={layout === "BudgetAllocator"}
                      totalFund={formatBudget(
                        idea.currentUserAllocation ||
                          idea.baItemConfig?.unitCap ||
                          0,
                        unitCode
                      )}
                      fundLocale={translate("budget.fundFor")}
                      fundedForLocale={translate("budget.fundedFor")}
                      undoBudgetingLocale={translate("budget.undo")}
                      hasTotalFunded={!!idea.currentUserAllocation}
                      showMoreOptions
                      actionList={
                        <ActionList menuList={[{}]} currentData={null} />
                      }
                    />
                  </Link>
                );
              })}
            </HorizontalScrollMenu>
          </Box>
        </>
      )}
      <Button
        variant="primary"
        mt={12}
        display="block"
        size={{ base: "lg", md: "md" }}
        width={{ base: "full", md: "auto" }}
        onClick={() =>
          window.open(
            `/${projectId}/brainstormers/${permalink}`,
            getRouteTarget()
          )
        }
        data-testid="ideas-banner-cta"
      >
        {toSentenceCase(callToAction.trim()) || translate("tools.haveYourSay")}
      </Button>
    </Box>
  );
};

export default Idea;
